import { useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, MouseEvent, PointerEvent } from 'react';
import type { ObservationWinRatePoint } from '../domain/observation-analysis';
import {
  observationTrendMoveAtRatio,
  observationTrendMoveTicks,
  observationTrendScoreLimit,
  observationTrendSegments,
  observationTrendTooltip,
} from '../domain/observation-trend-chart';
import './teacher-observation-trend-chart.css';

export interface TeacherObservationTrendChartProps {
  readonly points: readonly ObservationWinRatePoint[];
  readonly currentMove: number;
  readonly lastMoveNumber: number;
  readonly onSelectMove: (move: number) => void;
  readonly aiEnabled: boolean;
  readonly aiPending: boolean;
  readonly historyLayout?: boolean;
  readonly progressLabel?: string;
  readonly emptyLabel?: string;
  readonly onRetry?: () => void;
}

const WIDTH = 420;
const HEIGHT = 352;
const LEFT = 48;
const RIGHT = 382;
const TOP = 16;

export function TeacherObservationTrendChart({
  points,
  currentMove,
  lastMoveNumber,
  onSelectMove,
  aiEnabled,
  aiPending,
  historyLayout = false,
  progressLabel,
  emptyLabel,
  onRetry,
}: TeacherObservationTrendChartProps) {
  const helpId = useId();
  const plotRef = useRef<HTMLDivElement>(null);
  const [plotHeight, setPlotHeight] = useState(HEIGHT);
  const [plotWidth, setPlotWidth] = useState(WIDTH);
  const [hoverMove, setHoverMove] = useState<number | null>(null);
  const [keyboardMove, setKeyboardMove] = useState<number | null>(null);
  const lastMove = Math.max(0, lastMoveNumber);
  const segments = useMemo(() => observationTrendSegments(points, lastMove), [points, lastMove]);
  const visiblePoints = useMemo(() => segments.flat(), [segments]);
  const pointsByMove = useMemo(
    () => new Map(visiblePoints.map((point) => [point.moveNumber, point])),
    [visiblePoints],
  );
  const scoreLimit = observationTrendScoreLimit(visiblePoints);
  const moveTicks = observationTrendMoveTicks(lastMove);
  const selectedMove = Math.min(lastMove, Math.max(0, currentMove));
  const previewMove = Math.min(lastMove, Math.max(0, hoverMove ?? keyboardMove ?? selectedMove));
  const hasPreview = hoverMove !== null || keyboardMove !== null;
  const previewPoint = pointsByMove.get(previewMove);
  const selectedPoint = pointsByMove.get(selectedMove);
  const tooltip = observationTrendTooltip(previewMove, previewPoint);
  const bottom = plotHeight - 32;
  const chartWidth = historyLayout ? plotWidth : WIDTH;
  const right = historyLayout ? chartWidth - 38 : RIGHT;
  const moveX = (move: number) => LEFT + (move / Math.max(1, lastMove)) * (right - LEFT);
  const winRateY = (point: ObservationWinRatePoint) =>
    bottom - (Math.max(0, Math.min(10_000, point.blackWinRateBps)) / 10_000) * (bottom - TOP);
  const scoreY = (point: ObservationWinRatePoint) =>
    bottom -
    ((point.blackScoreLeadHalfPoints / 2 + scoreLimit) / (scoreLimit * 2)) * (bottom - TOP);

  // Fit the available card height without stretching the SVG text or hit coordinates.
  useLayoutEffect(() => {
    const plot = plotRef.current;
    if (plot === null) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry !== undefined && entry.contentRect.width > 0) {
        setPlotWidth(entry.contentRect.width);
        setPlotHeight(
          historyLayout
            ? entry.contentRect.height
            : (entry.contentRect.height / entry.contentRect.width) * WIDTH,
        );
      }
    });
    observer.observe(plot);
    return () => observer.disconnect();
  }, [historyLayout]);

  function moveFromPointer(event: PointerEvent<SVGSVGElement> | MouseEvent<SVGSVGElement>) {
    const bounds = event.currentTarget.getBoundingClientRect();
    const chartX = ((event.clientX - bounds.left) / Math.max(1, bounds.width)) * chartWidth;
    return observationTrendMoveAtRatio((chartX - LEFT) / (right - LEFT), lastMove);
  }

  function handleKeyDown(event: KeyboardEvent<SVGSVGElement>) {
    const move = keyboardMove ?? selectedMove;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      event.preventDefault();
      setHoverMove(null);
      setKeyboardMove(Math.max(0, move - 1));
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      event.preventDefault();
      setHoverMove(null);
      setKeyboardMove(Math.min(lastMove, move + 1));
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      setHoverMove(null);
      setKeyboardMove(event.key === 'Home' ? 0 : lastMove);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelectMove(Math.min(lastMove, Math.max(0, move)));
    } else if (event.key === 'Escape') {
      setHoverMove(null);
      setKeyboardMove(null);
    }
  }

  return (
    <section className="teacher-observation-trend" aria-label="胜率与目差走势图">
      <div className="teacher-observation-trend-legend">
        <span>
          <i className="is-win-rate" />
          黑胜率
        </span>
        <span>
          <i className="is-score" />
          {historyLayout ? '目差' : '黑领先目'}
        </span>
        {progressLabel !== undefined ? (
          <span className="review-analysis-progress" role="status">
            {progressLabel}
            {onRetry !== undefined ? (
              <button type="button" onClick={onRetry}>
                重试
              </button>
            ) : null}
          </span>
        ) : null}
      </div>
      <div className="teacher-observation-trend-plot" ref={plotRef}>
        <svg
          viewBox={`0 0 ${chartWidth} ${plotHeight}`}
          role="slider"
          aria-label="查看对局手数"
          aria-describedby={helpId}
          aria-valuemin={0}
          aria-valuemax={lastMove}
          aria-valuenow={previewMove}
          aria-valuetext={tooltip}
          tabIndex={0}
          onPointerMove={(event) => setHoverMove(moveFromPointer(event))}
          onPointerLeave={() => setHoverMove(null)}
          onClick={(event) => {
            const move = moveFromPointer(event);
            setKeyboardMove(move);
            onSelectMove(move);
          }}
          onFocus={() => setKeyboardMove(selectedMove)}
          onBlur={() => setKeyboardMove(null)}
          onKeyDown={handleKeyDown}
        >
          <title>黑方胜率与领先目数；点击任意位置跳转棋局</title>
          <rect
            className="teacher-observation-trend-surface"
            x={LEFT}
            y={TOP}
            width={right - LEFT}
            height={bottom - TOP}
          />
          {moveTicks.map((move) => (
            <line
              className="teacher-observation-trend-grid"
              key={move}
              x1={moveX(move)}
              x2={moveX(move)}
              y1={TOP}
              y2={bottom}
            />
          ))}
          {[0, 50, 100].map((percent) => {
            const y = bottom - (percent / 100) * (bottom - TOP);
            const score = (percent / 50 - 1) * scoreLimit;
            return (
              <g key={percent}>
                <line
                  className={`teacher-observation-trend-grid${percent === 50 ? ' is-center' : ''}`}
                  x1={LEFT}
                  x2={right}
                  y1={y}
                  y2={y}
                />
                <text
                  className="teacher-observation-trend-rate-label"
                  x={LEFT - 8}
                  y={y}
                  textAnchor="end"
                  dominantBaseline="middle"
                >
                  {percent}%
                </text>
                <text
                  className="teacher-observation-trend-score-label"
                  x={right + 8}
                  y={y}
                  dominantBaseline="middle"
                >
                  {score < 0 ? `−${Math.abs(score)}` : score > 0 ? `+${score}` : score}
                </text>
              </g>
            );
          })}
          <path
            className="teacher-observation-trend-rate-axis"
            d={`M${LEFT},${TOP}V${bottom}H${right}`}
          />
          <line
            className="teacher-observation-trend-score-axis"
            x1={right}
            x2={right}
            y1={TOP}
            y2={bottom}
          />
          {moveTicks.map((move) => (
            <text
              className="teacher-observation-trend-move-label"
              key={move}
              x={moveX(move)}
              y={bottom + 23}
              textAnchor="middle"
            >
              {move}
            </text>
          ))}
          {segments.map((segment) => (
            <g key={segment[0]?.moveNumber}>
              <polyline
                className="teacher-observation-trend-rate-line"
                points={segment
                  .map((point) => `${moveX(point.moveNumber)},${winRateY(point)}`)
                  .join(' ')}
              />
              <polyline
                className="teacher-observation-trend-score-line"
                points={segment
                  .map((point) => `${moveX(point.moveNumber)},${scoreY(point)}`)
                  .join(' ')}
              />
              {(visiblePoints.length <= 24 ? segment : segment.length === 1 ? segment : []).map(
                (point) => (
                  <g key={point.moveNumber}>
                    <circle
                      className="teacher-observation-trend-rate-dot"
                      cx={moveX(point.moveNumber)}
                      cy={winRateY(point)}
                      r={2.5}
                    />
                    <circle
                      className="teacher-observation-trend-score-dot"
                      cx={moveX(point.moveNumber)}
                      cy={scoreY(point)}
                      r={2.5}
                    />
                  </g>
                ),
              )}
            </g>
          ))}
          <line
            className="teacher-observation-trend-current"
            x1={moveX(selectedMove)}
            x2={moveX(selectedMove)}
            y1={TOP}
            y2={bottom}
          />
          {historyLayout && selectedPoint !== undefined && (
            <g className="review-trend-selection">
              <circle
                className="is-win-rate"
                cx={moveX(selectedMove)}
                cy={winRateY(selectedPoint)}
                r={6}
              />
              <circle
                className="is-score"
                cx={moveX(selectedMove)}
                cy={scoreY(selectedPoint)}
                r={6}
              />
              <rect
                x={Math.max(LEFT, Math.min(right - 70, moveX(selectedMove) - 35))}
                y={0}
                width={70}
                height={24}
                rx={5}
              />
              <text
                x={Math.max(LEFT + 35, Math.min(right - 35, moveX(selectedMove)))}
                y={16}
                textAnchor="middle"
              >
                第 {selectedMove} 手
              </text>
            </g>
          )}
          {hasPreview && (
            <g>
              <line
                className="teacher-observation-trend-crosshair"
                x1={moveX(previewMove)}
                x2={moveX(previewMove)}
                y1={TOP}
                y2={bottom}
              />
              {previewPoint !== undefined && (
                <>
                  <circle
                    className="teacher-observation-trend-hover is-win-rate"
                    cx={moveX(previewMove)}
                    cy={winRateY(previewPoint)}
                    r={5}
                  />
                  <circle
                    className="teacher-observation-trend-hover is-score"
                    cx={moveX(previewMove)}
                    cy={scoreY(previewPoint)}
                    r={5}
                  />
                </>
              )}
            </g>
          )}
        </svg>
        {visiblePoints.length === 0 && (
          <p className="teacher-observation-trend-empty" role="status">
            {emptyLabel ??
              (aiEnabled
                ? aiPending
                  ? '正在分析当前局面…'
                  : '当前局面尚未分析'
                : '开启 AI 分析后显示胜率与目差')}
          </p>
        )}
        {hasPreview && (
          <output
            className="teacher-observation-trend-tooltip"
            style={{
              left: `clamp(8px, calc(${(moveX(previewMove) / chartWidth) * 100}% - 82px), calc(100% - 172px))`,
            }}
          >
            <strong>第 {previewMove} 手</strong>
            {previewPoint === undefined ? (
              <span>尚未分析</span>
            ) : (
              <>
                <span>
                  黑胜率 <b>{(previewPoint.blackWinRateBps / 100).toFixed(1)}%</b>
                </span>
                <span>
                  黑领先{' '}
                  <b>
                    {previewPoint.blackScoreLeadHalfPoints < 0 ? '−' : ''}
                    {(Math.abs(previewPoint.blackScoreLeadHalfPoints) / 2).toFixed(1)} 目
                  </b>
                </span>
              </>
            )}
          </output>
        )}
      </div>
      <footer className="teacher-observation-trend-footer">
        <strong>
          {historyLayout
            ? `第 ${selectedMove} 手 / 全局 ${lastMove} 手`
            : `第 ${selectedMove} 手 · 当前局面`}
        </strong>
        <span>点击曲线跳转对应局面</span>
      </footer>
      <p id={helpId} className="teacher-observation-trend-help">
        移动鼠标查看胜率与目差，点击跳转该手。键盘方向键选择，回车跳转。
      </p>
    </section>
  );
}
